import * as React from "react";
import { useAsyncTaskLoading, usePrevious, useSnackBarCard } from "@/utils/hooks";
import api, { AreaInfoResponse, AreaInfo } from "@/apis/area";
import FormContext, { initFormStore, updateAddressIds, updateFormValues, updateUnits } from "@/contexts/Form";
import form, { FormValues, FormValidates, ifResponseValueBlank, FormAddress } from "@/utils/form";
import { defaultPrefs } from "@/constants/Prefecture";
import { PrefectureInfo } from "@/utils/prefecture";
import { isBlank, isNotNull } from "@/utils/is";
import ContractContext, { initContractStore } from "@/contexts/Contract";
import contract, {
  Feature,
  HIDDEN_COMPONENT_OPTIONS,
  isControlFull,
  isHiddenComponent,
  ValuationAreaExtras,
  ValuationAreaType,
} from "@/utils/contract";
import PropertyType, { queryParamsPropertyTypeMapping } from "@/constants/PropertyType";
import { PrefecturesInfoResponse } from "@/utils/prefecture";
import { CitiesInfoResponse } from "@/utils/city";
import { TownsInfoResponse } from "@/utils/town";
import propertyApi, {
  FetchMansionTrafficRequest,
  FetchMansionTrafficResponse,
  FetchMansionUnitsRequest,
  FetchMansionUnitsResponse,
  SearchMansionResponse,
} from "@/apis/property";
import { useQueryParams } from "@/utils/hooks";
import QueryKey from "@/constants/QueryKey";
import { useTheme } from "@/hooks/useTheme";

export type InjectProps = {
  values: FormValues;
  validates: FormValidates;
  addressIds: FormAddress;
  prefs: Option[];
  cities: Option[];
  towns: Option[];
  lines: Option[];
  stations: Option[];
  isValid: boolean;
  clearState: (...keys: (keyof FormValues)[]) => void;
  selectMansion: (
    mansion: SearchMansionResponse
  ) => (Promise<FetchMansionTrafficResponse[] | symbol> | Promise<FetchMansionUnitsResponse[] | symbol>)[];
  onManualValueTriggered: () => void;
};

const withEffect = (WrappedComponent: React.ElementType<{ onNext: () => void } & InjectProps>) => {
  return (props: { onNext: () => void }) => {
    const { isNewTheme } = useTheme();
    const [prefectures, setPrefectures] = React.useState<AreaInfo[]>([]);
    const [cities, setCities] = React.useState<AreaInfo[]>([]);
    const [towns, setTowns] = React.useState<AreaInfo[]>([]);
    const [lines, setLines] = React.useState<AreaInfo[]>([]);
    const [stations, setStations] = React.useState<AreaInfo[]>([]);
    const snackBarOpen = useSnackBarCard("alert");

    const {
      state: { contractId, featuresObj },
    } = React.useContext(ContractContext) || initContractStore(contract);

    const {
      state: { values, validates, addressIds },
      isRequiredProperty,
      isValidProperty,
      dispatch,
    } = React.useContext(FormContext) || initFormStore(form);
    const isTrafficInfoAutoLinked = featuresObj[Feature.TRAFFIC_INFO_AUTO_LINK]?.enabled;
    const isTotalStoryEnabled = !isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.TOTAL_STORY);
    const isStructureEnabled = !isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.STRUCTURE);

    const prevIsSame = usePrevious(values.isSame);

    const { result: prefecturesResult, start: fetchPrefectures } = useAsyncTaskLoading<
      PrefecturesInfoResponse,
      [number, ValuationAreaExtras | undefined]
    >(api.fetchPrefsAsync);

    const { result: citiesResult, start: fetchCities, error: fetchCitiesError } = useAsyncTaskLoading<
      CitiesInfoResponse,
      [number, number]
    >(api.fetchCitiesAsync);

    const { result: townsResult, start: fetchTowns, error: fetchTownsError } = useAsyncTaskLoading<
      TownsInfoResponse,
      [number, number, number]
    >(api.fetchTownsAsync);

    const { result: citiesAllResult, start: fetchCitiesAll, error: fetchCitiesAllError } = useAsyncTaskLoading<
      AreaInfoResponse,
      [number]
    >(api.fetchCitiesAllAsync);

    const { result: townsAllResult, start: fetchTownsAll, error: fetchTownsAllError } = useAsyncTaskLoading<
      AreaInfoResponse,
      [number]
    >(api.fetchTownsAllAsync);

    const { result: lineResult, start: fetchLines, error: fetchLinesError } = useAsyncTaskLoading<
      AreaInfoResponse,
      [number]
    >(api.fetchLinesAsync);

    const { result: stationResult, start: fetchStations, error: fetchStationError } = useAsyncTaskLoading<
      AreaInfoResponse,
      [number]
    >(api.fetchStationsAsync);

    const { result: trafficResult, start: fetchMansionTraffic, error: fetchMansionTrafficError } = useAsyncTaskLoading<
      FetchMansionTrafficResponse[],
      [FetchMansionTrafficRequest]
    >(propertyApi.fetchMansionTraffic);

    const { result: unitsResult, start: fetchMansionUnits, error: fetchMansionUnitsError } = useAsyncTaskLoading<
      FetchMansionUnitsResponse[],
      [FetchMansionUnitsRequest]
    >(propertyApi.fetchMansionUnits);

    const isValid =
      isRequiredProperty(isTrafficInfoAutoLinked, isTotalStoryEnabled, isStructureEnabled) &&
      isValidProperty(isTrafficInfoAutoLinked, isTotalStoryEnabled, isStructureEnabled);

    React.useEffect(() => {
      const valuationAreaFeatures = featuresObj[Feature.VALUATION_AREA];
      if (valuationAreaFeatures && contractId) {
        fetchPrefectures(contractId, valuationAreaFeatures?.control);
      } else {
        setPrefectures(defaultPrefs);
      }
    }, []);

    React.useEffect(() => {
      setPrefectures(() => []);
      setCities(() => []);
      const VALUATION_AREA = Boolean(featuresObj[Feature.VALUATION_AREA]);
      if (prefecturesResult && VALUATION_AREA) {
        const result: AreaInfoResponse = [];
        prefecturesResult.forEach(value => {
          if (values.propertyType === PropertyType.MANSION && value.mansion !== ValuationAreaType.NONE) {
            result.push({ id: value.prefectureId, name: value.name });
          } else if (values.propertyType === PropertyType.LAND && value.land !== ValuationAreaType.NONE) {
            result.push({ id: value.prefectureId, name: value.name });
          } else if (values.propertyType === PropertyType.HOUSE && value.house !== ValuationAreaType.NONE) {
            result.push({ id: value.prefectureId, name: value.name });
          }
        });
        setPrefectures(() => result);
      } else {
        setPrefectures(defaultPrefs);
      }
    }, [prefecturesResult, values.propertyType]);

    React.useEffect(() => {
      const current = prefectures.find(v => v.name === values.propertyPref);
      if (current && contractId) {
        if (isControlFull(featuresObj[Feature.VALUATION_AREA]?.control)) {
          fetchCities(contractId, current.id);
        } else {
          fetchCitiesAll(current.id);
        }
        fetchLines(current.id);
      } else {
        setCities(() => []);
      }
    }, [values.propertyPref, prefectures]);

    React.useEffect(() => {
      if (citiesResult) {
        const result: AreaInfoResponse = [];
        citiesResult.forEach(value => {
          if (values.propertyType === PropertyType.MANSION && value.mansion !== ValuationAreaType.NONE) {
            result.push({ id: value.cityId, name: value.name });
          } else if (values.propertyType === PropertyType.LAND && value.land !== ValuationAreaType.NONE) {
            result.push({ id: value.cityId, name: value.name });
          } else if (values.propertyType === PropertyType.HOUSE && value.house !== ValuationAreaType.NONE) {
            result.push({ id: value.cityId, name: value.name });
          }
        });
        setCities(() => result);
      }
      if (fetchCitiesError) {
        snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [citiesResult, fetchCitiesError]);

    React.useEffect(() => {
      if (citiesAllResult) {
        setCities(() => citiesAllResult);
      }
      if (fetchCitiesError) {
        snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [citiesAllResult, fetchCitiesAllError]);

    React.useEffect(() => {
      const prefCurrent = prefectures.find(v => v.name === values.propertyPref);
      const cityCurrent = cities.find(v => v.name === values.propertyCity);
      if (prefCurrent && cityCurrent && contractId) {
        if (isControlFull(featuresObj[Feature.VALUATION_AREA]?.control)) {
          fetchTowns(contractId, prefCurrent.id, cityCurrent.id);
        } else {
          fetchTownsAll(cityCurrent.id);
        }
      } else {
        setTowns(() => []);
      }
    }, [values.propertyCity, cities]);

    React.useEffect(() => {
      if (townsResult) {
        const result: AreaInfoResponse = [];
        townsResult.forEach(value => {
          if (values.propertyType === PropertyType.MANSION && value.mansion !== ValuationAreaType.NONE) {
            result.push({ id: value.townId, name: value.name });
          } else if (values.propertyType === PropertyType.LAND && value.land !== ValuationAreaType.NONE) {
            result.push({ id: value.townId, name: value.name });
          } else if (values.propertyType === PropertyType.HOUSE && value.house !== ValuationAreaType.NONE) {
            result.push({ id: value.townId, name: value.name });
          }
        });
        setTowns(() => result);
      }
      if (fetchCitiesError) {
        snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [townsResult, fetchTownsError]);

    React.useEffect(() => {
      if (townsAllResult) {
        setTowns(() => townsAllResult);
      }
      if (fetchCitiesError) {
        snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [townsAllResult, fetchTownsAllError]);

    React.useEffect(() => {
      if (lineResult) {
        setLines(() => lineResult);
      }
      if (fetchLinesError) {
        snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [lineResult, fetchLinesError]);

    React.useEffect(() => {
      if (stationResult) {
        setStations(() => stationResult);
      }
      if (fetchStationError) {
        snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [stationResult, fetchStationError]);

    React.useEffect(() => {
      if (lineResult) {
        setLines(() => lineResult);
      }
      if (fetchLinesError) {
        snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [lineResult, fetchLinesError]);

    React.useEffect(() => {
      if (stationResult) {
        setStations(() => stationResult);
      }
      if (fetchStationError) {
        snackBarOpen("エリア情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [stationResult, fetchStationError]);

    React.useEffect(() => {
      if (lines && values.line) {
        const current = lines.find(p => p.name === values.line);
        if (current) {
          fetchStations(current.id);
        }
      }
    }, [lines, values.line]);

    const prefOptions: Option[] = React.useMemo(() => prefectures.map(v => ({ label: v.name, value: v.name })), [
      prefectures,
    ]);
    const cityOptions: Option[] = React.useMemo(() => cities.map(v => ({ label: v.name, value: v.name })), [cities]);
    const townOptions: Option[] = React.useMemo(() => towns.map(v => ({ label: v.name, value: v.name })), [towns]);
    const lineOptions: Option[] = React.useMemo(() => lines.map(v => ({ label: v.name, value: v.name })), [lines]);
    const stationOptions: Option[] = React.useMemo(() => stations.map(v => ({ label: v.name, value: v.name })), [
      stations,
    ]);

    const clearState = React.useCallback((...keys: (keyof FormValues)[]) => {
      const formValues = keys.reduce((ret, key) => ({ ...ret, [key]: "" }), {});
      const formValidates = keys.reduce((ret, key) => ({ ...ret, [key]: "" }), {});
      dispatch(updateFormValues({ values: formValues, validates: formValidates }));
    }, []);

    const hiddenLivingAddressComponent = isHiddenComponent(featuresObj, HIDDEN_COMPONENT_OPTIONS.CUSTOMER_ADDRESS);

    /**
     * 売却物件の[所在地]変更時、
     * [お住いの住所]を更新する
     */
    React.useEffect(() => {
      if (!hiddenLivingAddressComponent) {
        if (values.isSame) {
          dispatch(
            updateFormValues({
              values: {
                pref: isBlank(validates.propertyPref) ? values.propertyPref : "",
                city: isBlank(validates.propertyCity) ? values.propertyCity : "",
                town: isBlank(validates.propertyTown) ? values.propertyTown : "",
                street: isBlank(validates.propertyStreet) ? values.propertyStreet : "",
                building: `${isBlank(validates.propertyName) ? values.propertyName : ""}${
                  isBlank(validates.roomNumber) ? values.roomNumber : ""
                }`,
              },
              validates: {
                pref: "",
                city: "",
                town: "",
                street: "",
                building: "",
              },
            })
          );
        } else if (values.isSame !== prevIsSame) {
          dispatch(
            updateFormValues({
              values: {
                pref: "",
                city: "",
                town: "",
                street: "",
                building: "",
              },
              validates: {
                pref: "",
                city: "",
                town: "",
                street: "",
                building: "",
              },
            })
          );
        }
      }
    }, [
      values.propertyName,
      values.propertyPref,
      values.propertyCity,
      values.propertyTown,
      values.propertyStreet,
      values.isSame,
      prevIsSame,
      hiddenLivingAddressComponent,
    ]);

    React.useEffect(() => {
      const currentPref = prefectures.find(v => v.name === values.propertyPref);
      const currentCity = cities.find(v => v.name === values.propertyCity);
      const currentTown = towns.find(v => v.name === values.propertyTown);
      dispatch(
        updateAddressIds({
          addressIds: {
            prefId: isNotNull(currentPref) ? currentPref.id : null,
            cityId: isNotNull(currentCity) ? currentCity.id : null,
            townId: isNotNull(currentTown) ? currentTown.id : null,
          },
        })
      );
    }, [values.propertyTown, towns]);

    const handleSelectMansion = React.useCallback((mansion: SearchMansionResponse) => {
      const trafficPromise = fetchMansionTraffic({ mansionId: mansion.id });
      const unitPromise = fetchMansionUnits({ mansionId: mansion.id });
      const propertyTown = ifResponseValueBlank(mansion.address.town).replace("ヶ", "ケ");
      const builtDateYear = mansion.builtDate ? mansion.builtDate.split("-")[0] : "";
      const builtDateMonth = mansion.builtDate ? mansion.builtDate.split("-")[1] : "";
      dispatch(
        updateFormValues({
          values: {
            propertyName: ifResponseValueBlank(mansion.name),
            propertyPref: ifResponseValueBlank(mansion.address.pref),
            propertyCity: ifResponseValueBlank(mansion.address.city),
            propertyTown,
            builtDateYear,
            builtDateMonth,
          },
          validates: {
            propertyName: "",
            propertyPref: "",
            propertyCity: "",
            propertyTown: "",
            line: "",
            station: "",
            bus: "",
            walk: "",
            builtDateYear: "",
            builtDateMonth: "",
          },
        })
      );
      return [trafficPromise, unitPromise];
    }, []);

    React.useEffect(() => {
      if (trafficResult && trafficResult.length) {
        const traffic = trafficResult[0];
        dispatch(
          updateFormValues({
            values: {
              line: ifResponseValueBlank(traffic.line),
              station: ifResponseValueBlank(traffic.station),
              bus: ifResponseValueBlank(traffic.bus),
              walk: ifResponseValueBlank(traffic.walk),
            },
            validates: {
              line: "",
              station: "",
              bus: "",
              walk: "",
            },
          })
        );
      }
      if (fetchMansionTrafficError) {
        snackBarOpen("マンション交通情報の検索に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [trafficResult, fetchMansionTrafficError]);

    React.useEffect(() => {
      if (unitsResult) {
        dispatch(updateUnits(unitsResult));
      }
      if (fetchMansionUnitsError) {
        snackBarOpen("住戸情報の取得に失敗しました。恐れ入りますが再度お試しください。");
      }
    }, [unitsResult, fetchMansionUnitsError]);

    const query = useQueryParams();
    let propertyType = query.get(QueryKey.PROPERTY_TYPE);
    propertyType = queryParamsPropertyTypeMapping(propertyType);
    const apartmentName = query.get(QueryKey.APARTMENT_NAME);
    const prefecture = query.get(QueryKey.PREFECTURE);
    const city = query.get(QueryKey.CITY);
    const town = query.get(QueryKey.TOWN);

    const [isQueryParamsEnabled, setQueryParamsEnabled] = React.useState<boolean>(
      isNotNull(propertyType) || isNotNull(apartmentName) || isNotNull(prefecture) || isNotNull(city) || isNotNull(town)
    );

    const getQueryParamsToFormValues = () => {
      const queryParamsToFormValues: Partial<FormValues> = {};
      const currentPropertyType = isNotNull(propertyType) ? propertyType : values.propertyType;
      const valuationAreaFeatures = featuresObj[Feature.VALUATION_AREA];
      if (valuationAreaFeatures) {
        if (isNotNull(prefecture) && prefecturesResult && !Number.isNaN(Number(prefecture))) {
          let queryPrefInResponse: PrefectureInfo | undefined = undefined;

          if (currentPropertyType === PropertyType.MANSION) {
            queryPrefInResponse = prefecturesResult?.find(
              prefItem => prefItem.prefectureId == Number(prefecture) && prefItem.mansion !== ValuationAreaType.NONE
            );
          }
          if (currentPropertyType === PropertyType.LAND) {
            queryPrefInResponse = prefecturesResult?.find(
              prefItem => prefItem.prefectureId == Number(prefecture) && prefItem.land !== ValuationAreaType.NONE
            );
          }
          if (currentPropertyType === PropertyType.HOUSE) {
            queryPrefInResponse = prefecturesResult?.find(
              prefItem => prefItem.prefectureId == Number(prefecture) && prefItem.house !== ValuationAreaType.NONE
            );
          }
          if (queryPrefInResponse && queryPrefInResponse.name) {
            queryParamsToFormValues.propertyPref = queryPrefInResponse?.name;
            localStorage.setItem("propertyPref", queryPrefInResponse?.name as string);
          }
        }
      } else {
        const queryPrefInResponse = defaultPrefs?.find(prefItem => prefItem.id == Number(prefecture));
        queryParamsToFormValues.propertyPref = queryPrefInResponse?.name;
        localStorage.setItem("propertyPref", queryPrefInResponse?.name as string);
      }
      switch (propertyType) {
        case PropertyType.HOUSE:
        case PropertyType.LAND: {
          if (isNotNull(city) && citiesResult && !Number.isNaN(Number(city))) {
            const queryCityInResponse = citiesResult?.find(cityItem => cityItem.cityId === Number(city));
            queryParamsToFormValues.propertyCity = queryCityInResponse?.name;
            localStorage.setItem("propertyCity", queryCityInResponse?.name as string);
          }
          if (isNotNull(town) && townsResult && !Number.isNaN(Number(town))) {
            const queryTownInResponse = townsResult?.find(townItem => townItem.townId == Number(town));
            queryParamsToFormValues.propertyTown = queryTownInResponse?.name;
            localStorage.setItem("propertyTown", queryTownInResponse?.name as string);
          }
          break;
        }
        case PropertyType.MANSION: {
          if (isNotNull(apartmentName)) {
            queryParamsToFormValues.propertyName = apartmentName;
          }
          break;
        }
        default:
          break;
      }

      return queryParamsToFormValues;
    };

    React.useEffect(() => {
      if (isNewTheme && isQueryParamsEnabled)
        dispatch(
          updateFormValues({
            values: {
              ...getQueryParamsToFormValues(),
            },
            validates: {
              propertyName: "",
              propertyPref: "",
              propertyCity: "",
              propertyTown: "",
            },
          })
        );
    }, [prefecturesResult, citiesResult, townsResult]);

    React.useEffect(() => {
      if (isNewTheme && isQueryParamsEnabled && prefectures.length > 0 && propertyType === values.propertyType) {
        const isQueryParamsPrefecturePresent = prefectures.find(pref => pref.id === Number(prefecture));
        if (isQueryParamsPrefecturePresent && isQueryParamsPrefecturePresent.id) {
          if (isNotNull(prefecture) && !Number.isNaN(Number(prefecture))) {
            fetchCities(contractId as number, Number(prefecture));
          }
          if (
            isNotNull(prefecture) &&
            isNotNull(city) &&
            !Number.isNaN(Number(prefecture)) &&
            !Number.isNaN(Number(city))
          ) {
            fetchTowns(contractId as number, Number(prefecture), Number(city));
          }
        }
      }
    }, [prefectures]);

    const handleManualValueTriggered = () => {
      setQueryParamsEnabled(false);
    };

    return (
      <WrappedComponent
        values={values}
        validates={validates}
        addressIds={addressIds}
        prefs={prefOptions}
        cities={cityOptions}
        towns={townOptions}
        lines={lineOptions}
        stations={stationOptions}
        isValid={isValid}
        clearState={clearState}
        selectMansion={handleSelectMansion}
        onManualValueTriggered={handleManualValueTriggered}
        {...props}
      />
    );
  };
};

export default withEffect;
